import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight, ArrowLeft, House, Users, Tag, ShieldCheck, CalendarCheck, Clock } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TownPage = () => {
  const { slug } = useParams();
  const [town, setTown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTown = async () => {
      try {
        const response = await axios.get(`${API}/towns/${slug}`);
        setTown(response.data);
      } catch (err) {
        console.error("Error fetching town:", err);
        setError("Town not found");
      } finally {
        setLoading(false);
      }
    };
    fetchTown();
  }, [slug]);

  const services = [
    { icon: House, title: "In-Home Estate Sales", desc: "Professional sales conducted in your home" },
    { icon: Users, title: "Living Estate Sales", desc: "Help with downsizing while you're still living" },
    { icon: Tag, title: "Professional Pricing", desc: "Fair market values for all items" },
    { icon: ShieldCheck, title: "Complete Service", desc: "We handle everything from start to finish" }
  ];

  const faqs = [
    {
      question: `Do you really come to ${town?.name || 'our area'}?`,
      answer: `Yes! ${town?.name || 'This community'} is within our service area. We regularly conduct estate sales throughout ${town?.state === 'ND' ? 'North Dakota' : 'Minnesota'} and the surrounding region.`
    },
    {
      question: "Do I need to clean the house first?",
      answer: "No. You don't need to sort, organize, or clean anything before we arrive. We handle homes exactly as they are."
    },
    {
      question: "How soon can you schedule a sale?",
      answer: "Typically 2-4 weeks from consultation to sale. We can sometimes accommodate urgent timelines based on availability."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-[#5C564D]">Loading...</p>
      </div>
    );
  }

  if (error || !town) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-3xl font-serif font-medium text-[#1C1914] mb-4">Location Not Found</h1>
          <p className="text-[#5C564D] mb-8">We couldn't find information for this location.</p>
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Service Areas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="town-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 text-[#5C564D] hover:text-[#2C4C3B] font-medium mb-8 transition-colors"
            data-testid="back-to-areas"
          >
            <ArrowLeft className="w-5 h-5" />
            All Service Areas
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MapPin weight="duotone" className="w-6 h-6 text-[#C5A059]" />
                <span className="text-sm font-medium text-[#C5A059]">{town.distance_from_fargo}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
                Estate Sales in {town.name}, {town.state}
              </h1>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                {town.intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:2182982420"
                  className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                  data-testid="town-phone-button"
                >
                  <Phone weight="fill" className="w-5 h-5" />
                  Call 218-298-2420
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                  data-testid="town-contact-button"
                >
                  Request Consultation
                </Link>
              </div>
            </div>
            <div className="bg-[#2C4C3B] p-8 md:p-12 rounded-lg text-white">
              <h2 className="text-2xl font-serif font-medium mb-6">Estate Sale Services in {town.name}</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CalendarCheck weight="duotone" className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                  <span>Estate sales today, tomorrow & this weekend in {town.name}</span>
                </li>
                <li className="flex items-start gap-3">
                  <House weight="duotone" className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                  <span>In-home estate sales conducted at your location</span>
                </li>
                <li className="flex items-start gap-3">
                  <Users weight="duotone" className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                  <span>Living estate sales for downsizing families</span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock weight="duotone" className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                  <span>No need to clean first—we handle everything</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-8">
            How We Help {town.name} Families
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-[#F7F5F0] p-6 rounded-lg"
                data-testid={`service-${index}`}
              >
                <service.icon weight="duotone" className="w-8 h-8 text-[#2C4C3B] mb-4" />
                <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2">{service.title}</h3>
                <p className="text-[#5C564D] text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-4">
            Nearby Communities We Serve
          </h2>
          <p className="text-[#5C564D] mb-8">
            In addition to {town.name}, we provide estate sale services to these nearby communities:
          </p>
          <div className="flex flex-wrap gap-3">
            {town.nearby_areas.map((area, index) => (
              <span
                key={index}
                className="bg-white border border-[#E2DCD0] px-4 py-2 rounded-full text-[#5C564D]"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-8">
            Estate Sale Questions for {town.name}
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-[#F7F5F0] border border-[#E2DCD0] rounded-lg px-6"
              >
                <AccordionTrigger
                  className="text-left text-[#1C1914] font-medium py-6 hover:no-underline"
                  data-testid={`town-faq-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C564D] pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium"
              data-testid="town-more-faqs"
            >
              View All FAQs
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* For Buyers */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#2C4C3B]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
            For Shoppers
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-white mb-6">
            Looking for Estate Sales in {town.name}?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Follow Legacy Liquidators LLC for updates on estate sales today, tomorrow, and this weekend in {town.name} and throughout the {town.state === 'ND' ? 'Fargo-Moorhead' : 'lakes'} region.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
            data-testid="town-blog-button"
          >
            Check Our Blog for Updates
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-[#1C1914] mb-6">
            Ready to Get Started in {town.name}?
          </h2>
          <p className="text-[#5C564D] mb-8">
            Contact Legacy Liquidators LLC today for a free consultation. We're here to help {town.name} families with compassionate estate sale services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="town-cta-phone"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="town-cta-contact"
            >
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/about" className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all group" data-testid="town-link-about">
              <h3 className="font-serif font-medium text-[#1C1914] group-hover:text-[#2C4C3B] transition-colors">About Us</h3>
              <p className="text-sm text-[#5C564D]">Learn about our team</p>
            </Link>
            <Link to="/faq" className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all group" data-testid="town-link-faq">
              <h3 className="font-serif font-medium text-[#1C1914] group-hover:text-[#2C4C3B] transition-colors">FAQ</h3>
              <p className="text-sm text-[#5C564D]">Common questions</p>
            </Link>
            <Link to="/blog" className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all group" data-testid="town-link-blog">
              <h3 className="font-serif font-medium text-[#1C1914] group-hover:text-[#2C4C3B] transition-colors">Blog</h3>
              <p className="text-sm text-[#5C564D]">Estate sale guides</p>
            </Link>
            <Link to="/contact" className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all group" data-testid="town-link-contact">
              <h3 className="font-serif font-medium text-[#1C1914] group-hover:text-[#2C4C3B] transition-colors">Contact</h3>
              <p className="text-sm text-[#5C564D]">Get in touch</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TownPage;
